import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Custom hook for intersection observer with optimizations for performance
 * Better alternative to react-is-visible for this application's needs
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Visibility threshold (0-1)
 * @param {string} options.rootMargin - Root margin for intersection calculation
 * @param {boolean} options.once - Only trigger once when element becomes visible
 * @param {number} options.debounceMs - Debounce time for rapid visibility changes
 * @param {boolean} options.disconnectOnVisible - Disconnect observer after first visibility (performance optimization)
 * @returns {Object} - { elementRef, isVisible, hasBeenVisible, wasVisible }
 */
function useIntersectionObserver(options = {}) {
  const {
    threshold = 0,
    rootMargin = '0px',
    once = false,
    debounceMs = 0,
    disconnectOnVisible = false
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const [wasVisible, setWasVisible] = useState(false);
  
  const elementRef = useRef();
  const observerRef = useRef();
  const debounceTimeoutRef = useRef();

  // Debounced visibility change handler
  const handleVisibilityChange = useCallback((isIntersecting) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (debounceMs > 0) {
      debounceTimeoutRef.current = setTimeout(() => {
        updateVisibility(isIntersecting);
      }, debounceMs);
    } else {
      updateVisibility(isIntersecting);
    }
  }, [debounceMs]);

  const updateVisibility = useCallback((isIntersecting) => {
    setIsVisible(isIntersecting);
    
    if (isIntersecting) {
      setHasBeenVisible(true);
      setWasVisible(true);
      
      // Disconnect observer if configured to do so (performance optimization)
      if ((once || disconnectOnVisible) && observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    } else {
      // Only update visibility to false if not in "once" mode
      if (!once) {
        setWasVisible(false);
      }
    }
  }, [once, disconnectOnVisible]);

  useEffect(() => {
    const element = elementRef.current;
    
    // Don't observe if element doesn't exist or if already been visible in "once" mode
    if (!element || (once && hasBeenVisible)) {
      return;
    }

    // Check if IntersectionObserver is supported
    if (!window.IntersectionObserver) {
      console.warn('IntersectionObserver not supported, defaulting to visible');
      setIsVisible(true);
      setHasBeenVisible(true);
      return;
    }

    // Create observer
    const observer = new IntersectionObserver(
      ([entry]) => {
        handleVisibilityChange(entry.isIntersecting);
      },
      {
        threshold,
        rootMargin,
        // Use document.documentElement as root for better compatibility
        root: null
      }
    );

    observerRef.current = observer;
    observer.observe(element);

    // Cleanup function
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [threshold, rootMargin, once, hasBeenVisible, handleVisibilityChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    elementRef,
    isVisible,
    hasBeenVisible,
    wasVisible
  };
}

export default useIntersectionObserver;